/**
 * ThreadMentionAutocompleteDropdown — presentational @-mention picker
 * (group-threading P2b follow-up). Purely presentational: the controller
 * (`useThreadMentionAutocomplete`) owns detection, the roster, and the insert; this
 * only renders the ranked candidates and forwards hover/select.
 *
 * Fixed-position + body-portaled so it floats just above the composer regardless of
 * the docked chat's transformed stacking context (same escape hatch the add-people
 * modal uses). Inert by contract: renders `null` unless enabled, open, anchored, and
 * non-empty — so it never paints when the mentions flag is off.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { MentionCandidate } from '../../services/threadMentions';
import { MENTION_ADD_COPY } from '../../services/threadMentions/mentionAddConsent';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';

export interface MentionAnchorRect {
  left: number;
  top: number;
  width: number;
}

export interface ThreadMentionAutocompleteDropdownProps {
  enabled?: boolean;
  open: boolean;
  candidates: MentionCandidate[];
  activeIndex: number;
  anchorRect: MentionAnchorRect | null;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
  /**
   * The ask (2026-09-15): the picked person is NOT in the thread and the
   * viewer may add people. While set, the list gives way to one question —
   * add them (and mention), or not. Owned by the controller, like the list.
   */
  pendingAdd?: MentionCandidate | null;
  onConfirmAdd?: () => void;
  onCancelAdd?: () => void;
  /** A refusal line (D9): the picked person is not in the thread and the viewer may not add people. */
  notice?: string | null;
  lang?: 'en' | 'es';
}

/**
 * The list's caption. It used to promise "Mention to add to this thread" —
 * the very side effect retired on 2026-09-15: a mention is a mention, and
 * adding someone is asked, never implied by a name.
 */
const HINT = MENTION_ADD_COPY.hint();

const cardStyle: React.CSSProperties = {
  position: 'fixed',
  background: 'var(--surface, #fff)',
  color: 'var(--text, #16181d)',
  border: '1px solid rgba(127,127,127,0.22)',
  borderRadius: 12,
  boxShadow: '0 10px 34px rgba(0,0,0,0.22)',
  maxHeight: 260,
  overflowY: 'auto',
  padding: '6px 0',
  // The chat's own portal rung — see MessageSenderChip (2026-09-11).
  zIndex: BODY_PORTAL_Z_INDEX,
};

const hintStyle: React.CSSProperties = {
  padding: '6px 14px 4px',
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  opacity: 0.5,
};

const rowBase: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: '7px 14px',
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const secondaryStyle: React.CSSProperties = { fontSize: 12, opacity: 0.6 };
const outsideStyle: React.CSSProperties = { fontSize: 12, color: 'var(--brand-ink-muted, #8a7a4a)' };

const askStyle: React.CSSProperties = { padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 };
const askLineStyle: React.CSSProperties = { fontSize: 14, lineHeight: 1.35 };
const askHintStyle: React.CSSProperties = { fontSize: 12, opacity: 0.62, lineHeight: 1.35 };
const askButtonsStyle: React.CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end' };
const askBtnStyle: React.CSSProperties = {
  border: '1px solid rgba(127,127,127,0.35)',
  background: 'transparent',
  color: 'inherit',
  borderRadius: 8,
  padding: '5px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
const askPrimaryBtnStyle: React.CSSProperties = {
  ...askBtnStyle,
  border: '1px solid rgba(198,167,94,0.7)',
  background: 'rgba(198,167,94,0.18)',
  color: '#8a6d2f',
  fontWeight: 600,
};
const noticeStyle: React.CSSProperties = {
  margin: '6px 10px 4px',
  padding: '7px 10px',
  borderRadius: 8,
  background: 'rgba(198,167,94,0.14)',
  color: '#8a6d2f',
  fontSize: 12,
  lineHeight: 1.35,
};

export const ThreadMentionAutocompleteDropdown: React.FC<
  ThreadMentionAutocompleteDropdownProps
> = ({ enabled, open, candidates, activeIndex, anchorRect, onSelect, onHover, pendingAdd, onConfirmAdd, onCancelAdd, notice, lang }) => {
  const es = lang === 'es';
  if (!enabled || !open || !anchorRect) return null;
  if (candidates.length === 0 && !pendingAdd && !notice) return null;

  const width = Math.min(340, Math.max(230, anchorRect.width));
  const style: React.CSSProperties = {
    ...cardStyle,
    left: Math.max(8, anchorRect.left),
    // Anchor the card's BOTTOM just above the composer's top so it grows upward.
    bottom: Math.max(8, window.innerHeight - anchorRect.top + 6),
    width,
  };

  const content = (
    <div
      style={style}
      role="listbox"
      aria-label={HINT}
      data-prest0n-portal="true"
      data-mention-autocomplete=""
    >
      {pendingAdd ? (
        // THE ASK (2026-09-15). The picked person is not in this thread. One
        // question, two answers; nothing is inserted or added until "Add and
        // mention". Cancel keeps the list, so a slip of the finger costs a click.
        <div style={askStyle} data-mention-add-ask={pendingAdd.uid} role="group" aria-label={MENTION_ADD_COPY.askAdd(pendingAdd.label, es)}>
          <span style={askLineStyle}>{MENTION_ADD_COPY.askAdd(pendingAdd.label, es)}</span>
          <span style={askHintStyle}>{MENTION_ADD_COPY.askAddHint(es)}</span>
          <span style={askButtonsStyle}>
            <button
              type="button"
              style={askBtnStyle}
              data-mention-add-cancel=""
              onMouseDown={(e) => { e.preventDefault(); onCancelAdd?.(); }}
            >
              {MENTION_ADD_COPY.cancel(es)}
            </button>
            <button
              type="button"
              style={askPrimaryBtnStyle}
              data-mention-add-confirm=""
              onMouseDown={(e) => { e.preventDefault(); onConfirmAdd?.(); }}
            >
              {MENTION_ADD_COPY.confirm(es)}
            </button>
          </span>
        </div>
      ) : (
        <>
          <div style={hintStyle}>{es ? MENTION_ADD_COPY.hint(true) : HINT}</div>
          {notice && (
            <div style={noticeStyle} role="alert" data-mention-notice="">
              {notice}
            </div>
          )}
          {candidates.map((c, i) => (
            <div
              key={c.uid}
              role="option"
              aria-selected={i === activeIndex}
              data-mention-option={c.uid}
              data-mention-in-thread={c.inThread === undefined ? undefined : c.inThread ? 'true' : 'false'}
              data-active={i === activeIndex ? 'true' : undefined}
              style={{
                ...rowBase,
                background: i === activeIndex ? 'rgba(198,167,94,0.16)' : 'transparent',
              }}
              // preventDefault keeps focus + selection in the composer so the insert can
              // target the live caret range; the click-select happens here on mousedown.
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(i);
              }}
              onMouseEnter={() => onHover(i)}
            >
              <span style={labelStyle} title={c.label}>
                {c.label}
              </span>
              {/* Who is NOT in the thread is said on the row (2026-09-15) — the
                  accidental mention is caught by the eye before the pick. */}
              {c.inThread === false ? (
                <span style={outsideStyle}>
                  {c.secondary ? `${c.secondary} · ` : ''}{MENTION_ADD_COPY.notInThread(es)}
                </span>
              ) : (
                c.secondary && <span style={secondaryStyle}>{c.secondary}</span>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? ReactDOM.createPortal(content, document.body)
    : content;
};

export default ThreadMentionAutocompleteDropdown;
